import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ShortcutLogo from '../components/ui/Shortcut';
import { RecordingStatus, useAudioRecording } from '../context/AudioRecordingContext';
import useAddNote from '../services/audioNote';
import { blobToFile } from '../utils/audio.util';
import { AudioFileDataWithoutBlob, getAudioBlob } from '../utils/indexedDB';

export interface NotificationAudioProps {
  audioData: AudioFileDataWithoutBlob;
}

const NotificationAudioRecorder: React.FC<NotificationAudioProps> = ({ audioData }) => {
  const navigate = useNavigate();
  const { addNote } = useAddNote();

  // Use the new dictionary-based context
  const {
    pauseRecording,
    resumeRecording,
    stopRecording,
    deleteRecording,
    formatTime,
    getRecordingStatus
  } = useAudioRecording();

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [processingAction, setProcessingAction] = useState(false);

  // Make sure patientId and Soapid are not undefined
  const patientId = audioData.patientId || 'unknown';
  const soapId = audioData.Soapid || 'unknown';
  const recordingId = `${patientId}:${soapId}`;

  // Get recording status specific to this patient
  const patientRecording = getRecordingStatus(patientId, soapId);

  // Use patient-specific status
  const isActiveRecording = patientRecording.isActive;
  const effectiveStatus = patientRecording.status;
  const displayTime = patientRecording.time;

    // maximum limit of recordings
      useEffect(() => {
        const handleRecordingTime = async () => {
        if (displayTime === 3000) {
            toast.info('You have 10 minutes left to record. The recording will be uploaded automatically after this.');
        }
        if (displayTime === 3300) {
          toast.info('You have 5 minutes left to record. The recording will be uploaded automatically after this.');
        }
        if (displayTime === 3600) {
          const toastid = "limit-finished"
            toast.info('Recording time is up. Uploading the recording now...',{toastId:toastid});
            await handleStop();

        }}
        handleRecordingTime();

      }, [displayTime]);

  // Handle pause/resume in the notification dropdown
  const handlePauseResume = async () => {
    // Prevent multiple clicks
    if (processingAction) return;

    try {
      setProcessingAction(true);

      if (effectiveStatus === RecordingStatus.RECORDING) {
        // If recording is active and recording, pause it
        await pauseRecording(recordingId);
      } else if (effectiveStatus === RecordingStatus.PAUSED) {
        // If recording is paused, resume it
        await resumeRecording(recordingId);
      }

      setProcessingAction(false);
    } catch (error) {
      setProcessingAction(false);
      console.error("Error toggling pause/resume:", error);
      toast.error("Failed to toggle recording state");
    }
  };

  // Handle stop
  const handleStop = async () => {
    if (processing || processingAction) {
      return;
    }

    try {
      setProcessing(true);
      setProcessingAction(true);

      // Show a loading toast
      const loadingToastId = toast.loading("Processing recording...");

      // First, stop the recording in the context
      const stopResult = await stopRecording(recordingId);

      if (!stopResult) {
        toast.update(loadingToastId, {
          render: "Recording must be at least 3 minutes long",
          type: "error",
          isLoading: false,
          autoClose: 3000
        });
        setProcessing(false);
        setProcessingAction(false);
        return;
      }

      // Then upload the SOAP note
      const audioBlob = await getAudioBlob(audioData);

      if (audioBlob) {
        const webmFile = blobToFile(audioBlob);

        // Upload to server
        addNote({
          patientId: audioData.patientId,
          caseId: audioData.Soapid,
          noteType: audioData.SoapType,
          file: webmFile
        }).subscribe({
          next: (res: any) => {
            // Delete local copy after successful upload
            deleteRecording(recordingId);

            // Update toast
            toast.update(loadingToastId, {
              render: res?.msg || "SOAP note generated successfully",
              type: "success",
              isLoading: false,
              autoClose: 3000
            });

            // Navigate back
            if (audioData.patientId === 'Anonymous') {
              navigate("/");
            } else {
              navigate(-1);
            }

            setProcessing(false);
            setProcessingAction(false);
          },
          error: (e: any) => {
            toast.update(loadingToastId, {
              render: e?.data?.msg || e.message || "Failed to generate SOAP note",
              type: "error",
              isLoading: false,
              autoClose: 3000
            });
            setProcessing(false);
            setProcessingAction(false);
          },
        });
      } else {
        toast.update(loadingToastId, {
          render: "Failed to retrieve audio data",
          type: "error",
          isLoading: false,
          autoClose: 3000
        });
        setProcessing(false);
        setProcessingAction(false);
      }
    } catch (error) {
      console.error("Error stopping recording:", error);
      toast.error("Failed to stop recording. Please try again.");
      setProcessing(false);
      setProcessingAction(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (processingAction) return;
    setProcessingAction(true);

    try {
      await deleteRecording(recordingId);
      setShowDeleteConfirm(false);
      toast.success("Recording discarded successfully");
      setProcessingAction(false);
    } catch (error) {
      setProcessingAction(false);
      console.error("Error deleting recording:", error);
      toast.error("Failed to delete recording");
    }
  };

  // Handle navigation to patient page
  const navigateToPatient = () => {
    if (audioData.patientName && audioData.patientId?.toLowerCase().includes('anonymous')) {
      navigate(`patients/cases/new-note-case/Anonymous`);
    } else if (audioData.patientId) {
      navigate(`/patient/case/${audioData.patientId}`);
    } else {
      navigate('/');
    }
  };

  return (
    <>
      <div className='flex-col gap-3'>
        <p
          className='text-sm capitalize cursor-pointer flex justify-between'
          onClick={navigateToPatient}
        >
          <span>{audioData.patientName || 'Unknown'}</span>
          <ShortcutLogo />
        </p>
        <span className='text-md flex items-center'>
          Status:
          <span className={`ml-1 font-medium ${effectiveStatus === RecordingStatus.RECORDING ? 'text-red-600' : ''}`}>
            {processing ? 'Processing' : effectiveStatus}
            {effectiveStatus === RecordingStatus.RECORDING && (
              <span className="inline-block ml-2 h-2 w-2 rounded-full bg-red-500 animate-pulse"></span>
            )}
            {processing && (
              <span className="inline-block ml-2 h-2 w-2 rounded-full bg-blue-500 animate-pulse"></span>
            )}
          </span>
        </span>
      </div>

      {showDeleteConfirm ? (
        <div className="my-2 rounded-md bg-red-50 p-3 text-center">
          <p className="mb-2 text-sm text-red-800">
            Are you sure you want to discard this recording?
          </p>
          <div className="flex justify-center gap-2">
            <button
              onClick={() => setShowDeleteConfirm(false)}
              className="rounded bg-gray-500 px-3 py-1 text-xs text-white hover:bg-gray-600"
              disabled={processingAction}
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              className="rounded bg-red-500 px-3 py-1 text-xs text-white hover:bg-red-600"
              disabled={processingAction}
            >
              Yes, Discard
            </button>
          </div>
        </div>
      ) : processing || processingAction ? (
        <div className="my-2 p-3 text-center">
          <div className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-solid border-blue-500 border-r-transparent align-[-0.125em]"></div>
          <p className="mt-1 text-xs text-gray-600">
            {processing ? 'Processing recording...' : 'Processing...'}
          </p>
        </div>
      ) : (
        <div className='flex justify-between'>
          <h3 className='py-3 text-2xl font-semibold'>{formatTime(displayTime)}</h3>

          <div className='flex justify-between gap-3 px-3 py-3'>
            {/* Delete Button - Now always enabled */}
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className='rounded bg-black p-2 text-white hover:bg-gray-800 disabled:opacity-50'
              aria-label="Delete recording"
              disabled={processingAction}
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='16'
                height='16'
                viewBox='0 0 24 24'
                fill='none'
              >
                <path
                  d='M10 12V17'
                  stroke='#ffffff'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
                <path
                  d='M14 12V17'
                  stroke='#ffffff'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
                <path
                  d='M4 7H20'
                  stroke='#ffffff'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
                <path
                  d='M6 10V18C6 19.6569 7.34315 21 9 21H15C16.6569 21 18 19.6569 18 18V10'
                  stroke='#ffffff'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
                <path
                  d='M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5V7H9V5Z'
                  stroke='#ffffff'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </button>

            {/* Pause/Resume Button */}
            <button
              onClick={handlePauseResume}
              className={`rounded p-2 text-white ${effectiveStatus === RecordingStatus.PAUSED ? 'bg-green-500' : 'bg-yellow-500'} disabled:opacity-50`}
              disabled={processingAction}
              aria-label={effectiveStatus === RecordingStatus.PAUSED ? "Resume recording" : "Pause recording"}
            >
              {effectiveStatus === RecordingStatus.PAUSED ? (
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='16px'
                  height='16px'
                  viewBox='0 0 24 24'
                  fill='none'
                >
                  <path
                    d='M21.4086 9.35258C23.5305 10.5065 23.5305 13.4935 21.4086 14.6474L8.59662 21.6145C6.53435 22.736 4 21.2763 4 18.9671L4 5.0329C4 2.72368 6.53435 1.26402 8.59661 2.38548L21.4086 9.35258Z'
                    fill='#1C274C'
                  />
                </svg>
              ) : (
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='16px'
                  height='16px'
                  viewBox='0 0 16 16'
                  fill='none'
                >
                  <path d='M7 1H2V15H7V1Z' fill='#000000' />
                  <path d='M14 1H9V15H14V1Z' fill='#000000' />
                </svg>
              )}
            </button>

            {/* Stop Button */}
            <button
              onClick={handleStop}
              className='rounded bg-red-500 p-2 text-white disabled:opacity-50'
              disabled={processingAction}
              aria-label="Stop recording"
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                fill='#ffffff'
                height='16'
                width='16'
                viewBox='0 0 24 24'
              >
                <path d='M6 6h12v12H6z' />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default NotificationAudioRecorder;
